import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../../api/client';
import { Plus, Edit2, Trash2, CheckCircle, XCircle, Award, Layers, DollarSign, Star, CreditCard, RefreshCw, AlertTriangle } from 'lucide-react';
import { showSuccess, showError } from '../../../lib/alerts';

interface Plan {
  id: string;
  name: string;
  slug?: string;
  price: string;
  currency: string;
  billingPeriod: string;
  maxUsers: number;
  maxWorkspaces: number;
  features: string[];
  isPopular: boolean;
  isActive: boolean;
  createdAt: string;
}

export function PlansAdminView() {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [price, setPrice] = useState('0.00');
  const [currency, setCurrency] = useState('BRL');
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [maxUsers, setMaxUsers] = useState(1);
  const [maxWorkspaces, setMaxWorkspaces] = useState(1);
  const [features, setFeatures] = useState('');
  const [isPopular, setIsPopular] = useState(false);
  const [isActive, setIsActive] = useState(true);

  // Query Plans
  const { data: plans = [], isLoading, isError, refetch } = useQuery<Plan[]>({
    queryKey: ['admin-plans'],
    queryFn: async () => {
      const res = await apiClient.get('/api/admin/plans');
      const payload = res.data?.data ?? res.data;
      return Array.isArray(payload) ? payload : [];
    },
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: any) => apiClient.post('/api/admin/plans', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] });
      setIsModalOpen(false);
      showSuccess('Plano criado com sucesso!');
    },
    onError: (err: any) => {
      showError(err?.response?.data?.message || err.message || 'Erro ao criar plano.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      apiClient.put(`/api/admin/plans/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] });
      setIsModalOpen(false);
      showSuccess('Plano atualizado com sucesso!');
    },
    onError: (err: any) => {
      showError(err?.response?.data?.message || err.message || 'Erro ao atualizar plano.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/api/admin/plans/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] });
      showSuccess('Plano excluído com sucesso!');
    },
    onError: (err: any) => {
      showError(err?.response?.data?.message || err.message || 'Erro ao excluir plano.');
    },
  });

  const openModal = (plan?: Plan) => {
    if (plan) {
      setEditingPlan(plan);
      setName(plan.name);
      setPrice(plan.price);
      setCurrency(plan.currency || 'BRL');
      setBillingPeriod(plan.billingPeriod || 'monthly');
      setMaxUsers(plan.maxUsers || 1);
      setMaxWorkspaces(plan.maxWorkspaces || 1);
      setFeatures(plan.features?.join(', ') || '');
      setIsPopular(!!plan.isPopular);
      setIsActive(plan.isActive !== undefined ? plan.isActive : true);
    } else {
      setEditingPlan(null);
      setName('');
      setPrice('49.00');
      setCurrency('BRL');
      setBillingPeriod('monthly');
      setMaxUsers(5);
      setMaxWorkspaces(2);
      setFeatures('Workspaces dedicados, Suporte 24/7, Dashboard inteligente');
      setIsPopular(false);
      setIsActive(true);
    }
    setIsModalOpen(true);
  };

  const handleSavePlan = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    const payload = {
      name,
      price: parseFloat(price) || 0,
      currency,
      billingPeriod,
      maxUsers: Number(maxUsers),
      maxWorkspaces: Number(maxWorkspaces),
      features: features.split(',').map((f) => f.trim()).filter(Boolean),
      isPopular,
      isActive,
    };

    if (editingPlan) {
      updateMutation.mutate({ id: editingPlan.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleDeletePlan = (id: string, planName: string) => {
    if (window.confirm(`Tem certeza que deseja excluir o plano "${planName}"?`)) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 max-w-[1400px] mx-auto pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-2">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-indigo-600 tracking-wider uppercase mb-1">
            <Layers size={14} /> Modelagem Comercial SaaS
          </div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            Planos & Assinaturas
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Configure tiers de precificação, limites de workspaces e recursos liberados para os tenants.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl text-xs font-medium shadow-xs transition-all"
          >
            <RefreshCw size={13} className={isLoading ? 'animate-spin' : ''} /> Atualizar
          </button>
          <button
            onClick={() => openModal()}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-900 hover:bg-black text-white rounded-xl text-xs font-semibold shadow-xs transition-all"
          >
            <Plus size={14} /> Novo Plano
          </button>
        </div>
      </div>

      {/* Plans Grid */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center p-16 bg-white rounded-2xl border border-gray-200">
          <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-xs text-gray-500 mt-3 font-medium">Carregando catálogo de planos...</p>
        </div>
      ) : isError ? (
        <div className="p-8 bg-white rounded-2xl border border-gray-200 text-center">
          <AlertTriangle size={32} className="mx-auto text-rose-500 mb-2" />
          <p className="text-sm font-semibold text-gray-800">Falha ao carregar planos</p>
          <button
            onClick={() => refetch()}
            className="mt-3 px-3 py-1.5 bg-indigo-50 text-indigo-600 text-xs font-medium rounded-lg hover:bg-indigo-100 transition-all"
          >
            Tentar Novamente
          </button>
        </div>
      ) : plans.length === 0 ? (
        <div className="p-16 bg-white rounded-2xl border border-gray-200 text-center">
          <Award size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-sm font-semibold text-gray-700">Nenhum plano cadastrado</p>
          <p className="text-xs text-gray-400 mt-1">Crie o primeiro plano para os clientes assinarem.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`bg-white rounded-2xl border p-6 flex flex-col justify-between relative shadow-xs hover:shadow-md transition-all ${
                plan.isPopular
                  ? 'border-indigo-500 ring-2 ring-indigo-500/20'
                  : 'border-gray-200/80'
              }`}
            >
              {plan.isPopular && (
                <div className="absolute -top-3 right-6 bg-indigo-600 text-white text-[10px] font-bold px-3 py-0.5 rounded-full uppercase tracking-wider shadow-xs flex items-center gap-1">
                  <Star size={10} className="fill-current" /> Mais Popular
                </div>
              )}

              <div>
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900">{plan.name}</h3>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      plan.isActive
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {plan.isActive ? 'Ativo' : 'Inativo'}
                  </span>
                </div>

                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-xs font-semibold text-gray-500">R$</span>
                  <span className="text-3xl font-extrabold text-gray-900 tracking-tight">
                    {parseFloat(plan.price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </span>
                  <span className="text-xs text-gray-400">
                    /{plan.billingPeriod === 'yearly' ? 'ano' : 'mês'}
                  </span>
                </div>

                <div className="mt-6 pt-4 border-t border-gray-100 space-y-2 text-xs text-gray-600">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Limite de Usuários:</span>
                    <span className="font-semibold text-gray-900">{plan.maxUsers || 'Ilimitado'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Workspaces Inclusos:</span>
                    <span className="font-semibold text-gray-900">{plan.maxWorkspaces || 'Ilimitado'}</span>
                  </div>
                </div>

                <div className="mt-5 space-y-2">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Recursos Inclusos</p>
                  <ul className="space-y-2 text-xs text-gray-600">
                    {(plan.features || []).map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle size={14} className="text-emerald-500 shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-8 pt-4 border-t border-gray-100 flex items-center justify-between">
                <button
                  onClick={() => openModal(plan)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all"
                >
                  <Edit2 size={13} /> Editar
                </button>
                <button
                  onClick={() => handleDeletePlan(plan.id, plan.name)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50 rounded-xl transition-all"
                >
                  <Trash2 size={13} /> Excluir
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Plan Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-100 animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold text-gray-900 mb-1">
              {editingPlan ? 'Editar Plano' : 'Novo Plano'}
            </h2>
            <p className="text-xs text-gray-500 mb-4">
              Configure as regras e valores do plano comercial.
            </p>

            <form onSubmit={handleSavePlan} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Nome do Plano</label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: Pro Anual"
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-hidden focus:bg-white focus:border-indigo-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Preço (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-hidden focus:bg-white focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Periodicidade</label>
                  <select
                    value={billingPeriod}
                    onChange={(e) => setBillingPeriod(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-medium text-gray-700 focus:outline-hidden"
                  >
                    <option value="monthly">Mensal</option>
                    <option value="yearly">Anual</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Máx. Usuários</label>
                  <input
                    type="number"
                    value={maxUsers}
                    onChange={(e) => setMaxUsers(Number(e.target.value))}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-hidden focus:bg-white focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Máx. Workspaces</label>
                  <input
                    type="number"
                    value={maxWorkspaces}
                    onChange={(e) => setMaxWorkspaces(Number(e.target.value))}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-hidden focus:bg-white focus:border-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Recursos (separados por vírgula)
                </label>
                <textarea
                  rows={3}
                  value={features}
                  onChange={(e) => setFeatures(e.target.value)}
                  placeholder="Até 10 usuários, Módulos Avançados, Suporte 24/7"
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-hidden focus:bg-white focus:border-indigo-500"
                />
              </div>

              <div className="flex items-center gap-6 pt-2">
                <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-gray-700">
                  <input
                    type="checkbox"
                    checked={isPopular}
                    onChange={(e) => setIsPopular(e.target.checked)}
                    className="rounded-md border-gray-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                  />
                  Destacar como Popular
                </label>

                <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-gray-700">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    className="rounded-md border-gray-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                  />
                  Plano Ativo
                </label>
              </div>

              <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition-all"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-all disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending ? 'Salvando...' : 'Salvar Plano'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
